import React from 'react';
import { motion } from 'framer-motion';
import { 
  Bot, 
  Clock, 
  Play, 
  Pause, 
  CheckCircle, 
  Timer,
  Zap,
  AlertCircle
} from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useAutomationStatus } from '../hooks/useAutomationStatus';

interface AutomationIndicatorProps {
  conversationId: string;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  showTimer?: boolean;
  className?: string;
}

export const AutomationIndicator: React.FC<AutomationIndicatorProps> = ({
  conversationId,
  size = 'sm',
  showLabel = false,
  showTimer = false,
  className
}) => {
  const { 
    automationStatus, 
    loading, 
    getStatusLabel, 
    getTimeToNextMessage 
  } = useAutomationStatus(conversationId);

  // Don't render anything if no automation is configured
  if (!automationStatus && !loading) {
    return null;
  }

  if (loading) {
    return (
      <div className={cn(
        "animate-pulse rounded-full bg-gray-200",
        size === 'sm' && "w-3 h-3",
        size === 'md' && "w-4 h-4", 
        size === 'lg' && "w-5 h-5",
        className
      )} />
    );
  }

  const status = automationStatus?.status || 'not_configured';
  const timeToNext = showTimer ? getTimeToNextMessage() : null;

  const statusConfigs = {
    active: {
      icon: Play,
      color: 'bg-green-500',
      textColor: 'text-green-600',
      borderColor: 'border-green-200',
      bgColor: 'bg-green-50',
      pulse: true,
      tooltip: 'AI automation is actively sending messages'
    },
    waiting: {
      icon: Clock,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-200',
      bgColor: 'bg-blue-50',
      pulse: true,
      tooltip: 'AI automation is waiting for the next response'
    },
    dormant: {
      icon: Pause,
      color: 'bg-yellow-500',
      textColor: 'text-yellow-600',
      borderColor: 'border-yellow-200',
      bgColor: 'bg-yellow-50',
      pulse: false,
      tooltip: 'AI automation is configured but paused'
    },
         completed: {
       icon: CheckCircle,
       color: 'bg-purple-500',
       textColor: 'text-purple-600',
       borderColor: 'border-purple-200',
       bgColor: 'bg-purple-50',
       pulse: false,
       tooltip: 'AI automation has completed all responses'
     },
     requires_human: {
       icon: AlertCircle,
       color: 'bg-red-500',
       textColor: 'text-red-600',
       borderColor: 'border-red-200',
       bgColor: 'bg-red-50',
       pulse: true,
       tooltip: 'Requires immediate human intervention - escalation detected'
     },
     not_configured: {
       icon: Bot,
       color: 'bg-gray-400',
       textColor: 'text-gray-600',
       borderColor: 'border-gray-200',
       bgColor: 'bg-gray-50',
       pulse: false,
       tooltip: 'No automation configured'
     }
  };

  const config = statusConfigs[status as keyof typeof statusConfigs] || statusConfigs.not_configured;
  const Icon = config.icon;

  const sizeClasses = {
    sm: {
      container: "w-6 h-6",
      icon: "w-3 h-3",
      text: "text-xs",
      padding: "px-2 py-1"
    },
    md: {
      container: "w-8 h-8",
      icon: "w-4 h-4",
      text: "text-sm",
      padding: "px-3 py-1.5"
    },
    lg: {
      container: "w-10 h-10",
      icon: "w-5 h-5",
      text: "text-base",
      padding: "px-4 py-2"
    }
  };

  const sizes = sizeClasses[size];

  // Simple dot indicator
  if (!showLabel && !showTimer) {
    return (
      <div 
        className={cn(className)}
        title={config.tooltip}
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="relative"
        >
          <div className={cn(
            "rounded-full flex items-center justify-center shadow-sm",
            sizes.container,
            config.color
          )}>
            <Icon className={cn(sizes.icon, "text-white")} />
          </div>
          
          {config.pulse && (
            <motion.div
              className={cn(
                "absolute inset-0 rounded-full",
                config.color,
                "opacity-20"
              )}
              animate={{ scale: [1, status === 'requires_human' ? 2 : 1.5, 1] }}
              transition={{ 
                duration: status === 'requires_human' ? 1 : 2, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
            />
          )}
          
          {/* Next message timer overlay */}
          {timeToNext && status === 'active' && (
            <motion.div
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute -bottom-1 -right-1 bg-blue-500 text-white text-xs rounded-full px-1 py-0.5 shadow-sm"
              style={{ fontSize: '8px', lineHeight: '10px' }}
            >
              <Timer className="w-2 h-2 inline mr-0.5" />
              {timeToNext.includes('m') ? timeToNext.split('m')[0] + 'm' : timeToNext.replace('s', '')}
            </motion.div>
          )}
        </motion.div>
      </div>
    );
  }

  // Badge-style indicator with label
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={cn(
        "inline-flex items-center space-x-1.5 rounded-full border",
        config.borderColor,
        config.bgColor,
        sizes.padding,
        className
      )}
      title={config.tooltip}
    >
      <div className="relative">
        <div className={cn(
          "rounded-full flex items-center justify-center",
          size === 'sm' && "w-3 h-3",
          size === 'md' && "w-4 h-4",
          size === 'lg' && "w-5 h-5",
          config.color
        )}>
          <Icon className={cn(
            size === 'sm' && "w-2 h-2",
            size === 'md' && "w-2.5 h-2.5",
            size === 'lg' && "w-3 h-3",
            "text-white"
          )} />
        </div>
        
                 {config.pulse && (
           <motion.div
             className={cn(
               "absolute inset-0 rounded-full",
               config.color,
               "opacity-20"
             )}
             animate={{ scale: [1, status === 'requires_human' ? 1.8 : 1.4, 1] }}
             transition={{ 
               duration: status === 'requires_human' ? 1 : 2, 
               repeat: Infinity, 
               ease: "easeInOut" 
             }}
           />
         )}
      </div>

      {showLabel && (
        <span className={cn(
          "font-medium",
          config.textColor,
          sizes.text
        )}>
          {automationStatus?.activationType === 'immediate' ? 'Active Now' : 'On Response'}
        </span>
      )}

      {showTimer && timeToNext && (
        <div className={cn(
          "flex items-center space-x-0.5",
          config.textColor,
          sizes.text,
          "font-mono"
        )}>
          <Timer className={cn(
            size === 'sm' && "w-2.5 h-2.5",
            size === 'md' && "w-3 h-3",
            size === 'lg' && "w-3.5 h-3.5"
          )} />
          <span>{timeToNext}</span>
        </div>
      )}
    </motion.div>
  );
};

// Compact version for tight spaces (conversation list)
export const AutomationDot: React.FC<{
  conversationId: string;
  className?: string;
}> = ({ conversationId, className }) => (
  <AutomationIndicator 
    conversationId={conversationId}
    size="sm"
    showLabel={false}
    showTimer={false}
    className={className}
  />
);

// Badge version for headers
export const AutomationBadge: React.FC<{
  conversationId: string;
  showTimer?: boolean;
  className?: string;
}> = ({ conversationId, showTimer = false, className }) => (
  <AutomationIndicator 
    conversationId={conversationId}
    size="sm"
    showLabel={true}
    showTimer={showTimer}
    className={className}
  />
); 